import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import UserAvatar from "./UserAvatar";
import { Heart, MessageCircle, Share2, Copy, Check, ExternalLink, Loader2, Sparkles, MoreHorizontal, Flag, Ban, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const REPORT_REASONS = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "other", label: "Other" },
];

function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function PostCard({ post, onHidden }) {
  // useAuth() is null when rendered outside an AuthProvider (e.g. unit
  // tests); treat that as an unknown viewer rather than crashing.
  const auth = useAuth();
  const viewer = auth?.user || null;
  const isOwnPost = !!(viewer && post.author_id && viewer.id === post.author_id);

  const [liked, setLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(post.recent_comments || []);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [likeAnimating, setLikeAnimating] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [allCommentsLoaded, setAllCommentsLoaded] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // Moderation: overflow menu, report dialog, block/mute confirm
  const [showMenu, setShowMenu] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "block" | "mute" | null
  const [confirmBusy, setConfirmBusy] = useState(false);
  const shareRef = useRef(null);
  const menuRef = useRef(null);
  const commentSectionRef = useRef(null);

  // Sync local state when the parent passes an updated post (e.g. after a
  // refresh / pagination merge). Without this the card would freeze on its
  // initial like/comment counts.
  useEffect(() => {
    setLiked(post.is_liked);
    setLikesCount(post.likes_count || 0);
    setCommentsCount(post.comments_count || 0);
  }, [post.id, post.is_liked, post.likes_count, post.comments_count]);

  // Close share dropdown on outside click
  useEffect(() => {
    if (!showShare) return;
    const close = (e) => { if (shareRef.current && !shareRef.current.contains(e.target)) setShowShare(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showShare]);

  // Close the overflow ("…") menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showMenu]);

  const handleLike = async () => {
    // Optimistic update
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikesCount((c) => wasLiked ? Math.max(0, c - 1) : c + 1);
    if (!wasLiked) { setLikeAnimating(true); setTimeout(() => setLikeAnimating(false), 400); }
    try {
      await api.post(`/posts/${post.id}/like`);
    } catch {
      setLiked(wasLiked);
      setLikesCount((c) => wasLiked ? c + 1 : Math.max(0, c - 1));
      toast.error("Failed to like");
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || sendingComment) return;
    setSendingComment(true);
    try {
      const r = await api.post(`/posts/${post.id}/comments`, { content: commentText.trim() });
      setComments((prev) => [...prev, r.data]);
      setCommentsCount((c) => c + 1);
      setCommentText("");
    } catch { toast.error("Failed to add comment"); }
    finally { setSendingComment(false); }
  };

  const toggleComments = async () => {
    if (!showComments && !allCommentsLoaded) {
      try {
        const r = await api.get(`/posts/${post.id}/comments?page=1&limit=20`);
        setComments(r.data?.comments || []);
        setAllCommentsLoaded(true);
      } catch { /* keep existing */ }
    }
    setShowComments(!showComments);
  };

  // Deep-link to the post via the feed query param so recipients of a
  // shared link land on the actual post, not just the feed root.
  const postUrl = `${window.location.origin}/feed?post=${post.id}`;

  const handleSuggestComments = async () => {
    setLoadingSuggestions(true);
    try {
      const r = await api.post("/ai-studio/suggest-comments", { post_content: post.content_text });
      setAiSuggestions(r.data.suggestions || []);
    } catch { toast.error("Suggestions failed"); }
    finally { setLoadingSuggestions(false); }
  };
  const shareText = `${post.content_text?.slice(0, 100)}...`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(postUrl);
    toast.success("Link copied!");
    setShowShare(false);
  };

  const handleShareSocial = (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(postUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(postUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(postUrl)}`,
    };
    window.open(urls[platform], "_blank", "width=600,height=400");
    setShowShare(false);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportReason || reportSubmitting) return;
    setReportSubmitting(true);
    try {
      const body = { reason: reportReason };
      if (reportDetails.trim()) body.details = reportDetails.trim();
      const r = await api.post(`/posts/${post.id}/report`, body);
      if (r.data?.already_reported) toast.info("You've already reported this post");
      else toast.success("Report submitted. Our moderators will review it.");
      setShowReport(false);
      setReportReason("");
      setReportDetails("");
    } catch { toast.error("Failed to submit report"); }
    finally { setReportSubmitting(false); }
  };

  const handleBlockMute = async () => {
    const action = confirmAction;
    if (!action || confirmBusy) return;
    setConfirmBusy(true);
    try {
      await api.post(`/users/${post.author_username}/${action}`);
      setConfirmAction(null);
      const verb = action === "block" ? "Blocked" : "Muted";
      if (onHidden) {
        toast.success(`${verb} @${post.author_username}`);
        // Feed refetch / hiding is the parent's job.
        onHidden(post, action);
      } else {
        toast.success(`${verb} @${post.author_username}. Refresh your feed to hide their posts.`);
      }
    } catch { toast.error(`Failed to ${action} user`); }
    finally { setConfirmBusy(false); }
  };

  return (
    <div className={`bg-card rounded-xl border border-border/60 shadow-sm overflow-hidden animate-fade-in-up ${post.ai_assistant_name ? "border-l-ai" : post.is_challenge ? "border-l-challenge" : "border-l-user"}`} data-testid={`post-card-${post.id}`}>
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-start gap-3">
        <Link to={`/profile/${post.author_username}`} className="flex-shrink-0">
          <UserAvatar name={post.author_display_name} username={post.author_username} avatarUrl={post.author_avatar_url} size="md" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={`/profile/${post.author_username}`} className="text-sm font-semibold text-foreground no-underline hover:text-cyan-400 transition-colors">{post.author_display_name}</Link>
            <Link to={`/profile/${post.author_username}`} className="text-xs text-muted-foreground/70 no-underline">@{post.author_username}</Link>
            <span className="text-xs text-muted-foreground/70">&middot;</span>
            <span className="text-xs text-muted-foreground/70">{formatRelativeTime(post.created_at)}</span>
          </div>
          {post.ai_assistant_name && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 mt-1 font-medium border border-violet-500/20">
              Your companion {post.ai_assistant_name} shared this
            </span>
          )}
        </div>
        {/* Overflow menu */}
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1.5 rounded-lg text-muted-foreground/70 hover:text-muted-foreground hover:bg-white/5 bg-transparent border-0 cursor-pointer transition-colors"
            aria-label="Post options"
            data-testid={`post-menu-btn-${post.id}`}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg p-1.5 w-48 z-20 animate-fade-in" data-testid={`post-menu-${post.id}`}>
              <button
                onClick={() => { setShowMenu(false); setShowReport(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground/80 hover:bg-white/5 rounded-lg bg-transparent border-0 cursor-pointer transition-colors"
                data-testid={`report-post-btn-${post.id}`}
              >
                <Flag className="w-3.5 h-3.5" /> Report post
              </button>
              {!isOwnPost && (
                <>
                  <button
                    onClick={() => { setShowMenu(false); setConfirmAction("block"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-red-500/10 rounded-lg bg-transparent border-0 cursor-pointer transition-colors"
                    data-testid={`block-user-btn-${post.id}`}
                  >
                    <Ban className="w-3.5 h-3.5" /> Block @{post.author_username}
                  </button>
                  <button
                    onClick={() => { setShowMenu(false); setConfirmAction("mute"); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground/80 hover:bg-white/5 rounded-lg bg-transparent border-0 cursor-pointer transition-colors"
                    data-testid={`mute-user-btn-${post.id}`}
                  >
                    <VolumeX className="w-3.5 h-3.5" /> Mute @{post.author_username}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{post.content_text}</p>
        {post.cross_posted_to && post.cross_posted_to.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2 text-[10px] text-cyan-400 font-medium" data-testid="cross-post-badge">
            <span>Also posted to</span>
            {post.cross_posted_to.map((p) => (
              <span key={p} className={`px-1.5 py-0.5 rounded text-white text-[9px] ${p === "youtube" ? "bg-red-600" : p === "tiktok" ? "bg-gray-900" : "bg-gradient-to-r from-purple-500 to-pink-500"}`}>
                {p === "youtube" ? "▶ YT" : p === "tiktok" ? "♪ TT" : "📷 IG"}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Images */}
      {post.images && post.images.length > 0 && (
        <div className={`grid gap-0.5 ${post.images.length === 1 ? "" : "grid-cols-2"}`}>
          {post.images.slice(0, 4).map((img, i) => (
            <img key={i} src={img} alt="" className="w-full h-48 object-cover" />
          ))}
        </div>
      )}

      {/* Action Bar */}
      <div className="px-4 py-2 border-t border-border/40 flex items-center gap-1">
        {/* Like */}
        <button
          onClick={handleLike}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all bg-transparent border-0 cursor-pointer ${
            liked ? "text-red-500" : "text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
          }`}
          data-testid={`like-btn-${post.id}`}
        >
          <Heart
            className={`w-4 h-4 transition-all duration-300 ${liked ? "fill-red-500 text-red-500" : ""} ${likeAnimating ? "scale-[1.4]" : "scale-100"}`}
            style={likeAnimating ? { filter: "drop-shadow(0 0 4px rgba(239,68,68,0.5))" } : {}}
          />
          {likesCount > 0 && <span>{likesCount}</span>}
        </button>
        {/* Comment */}
        <button
          onClick={toggleComments}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all bg-transparent border-0 cursor-pointer ${
            showComments ? "text-cyan-400 bg-cyan-500/10" : "text-muted-foreground hover:text-cyan-400 hover:bg-cyan-500/10"
          }`}
          data-testid={`comment-btn-${post.id}`}
        >
          <MessageCircle className="w-4 h-4" />
          {commentsCount > 0 && <span>{commentsCount}</span>}
        </button>
        {/* Share */}
        <div className="relative" ref={shareRef}>
          <button
            onClick={() => setShowShare(!showShare)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 bg-transparent border-0 cursor-pointer transition-all"
            data-testid={`share-btn-${post.id}`}
          >
            <Share2 className="w-4 h-4" />
          </button>
          {showShare && (
            <div className="absolute bottom-full left-0 mb-1 bg-card border border-border rounded-xl shadow-lg p-1.5 w-48 z-20 animate-fade-in" data-testid={`share-dropdown-${post.id}`}>
              <button onClick={handleCopyLink} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground/80 hover:bg-white/5 rounded-lg bg-transparent border-0 cursor-pointer transition-colors">
                <Copy className="w-3.5 h-3.5" /> Copy Link
              </button>
              <button onClick={() => handleShareSocial("twitter")} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground/80 hover:bg-white/5 rounded-lg bg-transparent border-0 cursor-pointer transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Share to X / Twitter
              </button>
              <button onClick={() => handleShareSocial("facebook")} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground/80 hover:bg-white/5 rounded-lg bg-transparent border-0 cursor-pointer transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Share to Facebook
              </button>
              <button onClick={() => handleShareSocial("linkedin")} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground/80 hover:bg-white/5 rounded-lg bg-transparent border-0 cursor-pointer transition-colors">
                <ExternalLink className="w-3.5 h-3.5" /> Share to LinkedIn
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Comments Section */}
      <div
        style={{
          maxHeight: showComments ? "500px" : "0px",
          opacity: showComments ? 1 : 0,
          transition: "max-height 0.3s ease-out, opacity 0.2s ease-out",
          overflow: "hidden",
        }}
        ref={commentSectionRef}
      >
        <div className="border-t border-border/40 px-4 py-3 bg-white/[0.03]" data-testid={`comments-section-${post.id}`}>
          {comments.length === 0 && <p className="text-xs text-muted-foreground/70 text-center py-2">No comments yet. Be the first!</p>}
          <div className="space-y-3 mb-3">
            {comments.map((c, i) => (
              <div key={c.id || i} className="flex gap-2 animate-fade-in">
                <UserAvatar name={c.author_display_name} username={c.author_username} avatarUrl={c.author_avatar_url} size="xs" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Link to={`/profile/${c.author_username}`} className="text-xs font-semibold text-foreground no-underline hover:text-cyan-400">{c.author_display_name}</Link>
                    <span className="text-[10px] text-muted-foreground/70">{formatRelativeTime(c.created_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.content}</p>
                </div>
              </div>
            ))}
          </div>
          {/* AI Suggestion Chips */}
          {aiSuggestions.length > 0 && (
            <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1" data-testid={`ai-suggestions-${post.id}`}>
              {aiSuggestions.map((s, i) => (
                <button key={i} onClick={() => { setCommentText(s); setAiSuggestions([]); }} className="flex-shrink-0 px-3 py-1.5 rounded-full bg-cyan-500/10 text-cyan-400 text-[11px] border border-cyan-500/20 cursor-pointer hover:bg-cyan-500/15 transition-colors animate-fade-in" style={{ animationDelay: `${i * 80}ms` }} data-testid={`suggestion-chip-${i}`}>
                  {s.length > 50 ? s.slice(0, 50) + "..." : s}
                </button>
              ))}
            </div>
          )}
          <form onSubmit={handleComment} className="flex gap-2 items-center">
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 bg-card border border-border rounded-full text-xs text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400"
              data-testid={`comment-input-${post.id}`}
            />
            <button
              type="button"
              onClick={handleSuggestComments}
              disabled={loadingSuggestions}
              className="w-8 h-8 rounded-full bg-violet-500/10 text-violet-500 flex items-center justify-center border-0 cursor-pointer hover:bg-violet-500/15 transition-colors flex-shrink-0 disabled:opacity-40"
              data-testid={`ai-suggest-btn-${post.id}`}
              title="AI Suggest"
            >
              {loadingSuggestions ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            </button>
            <button
              type="submit"
              disabled={!commentText.trim() || sendingComment}
              className="w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center border-0 cursor-pointer disabled:opacity-40 hover:bg-indigo-600 transition-colors flex-shrink-0"
              data-testid={`comment-submit-${post.id}`}
            >
              {sendingComment ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageCircle className="w-3 h-3" />}
            </button>
          </form>
        </div>
      </div>

      {/* Report Dialog */}
      {showReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowReport(false)} data-testid={`report-dialog-${post.id}`}>
          <div className="bg-card rounded-2xl shadow-xl max-w-sm w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Flag className="w-4 h-4 text-red-500" />
              <h2 className="font-outfit text-lg font-semibold text-foreground">Report post</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Tell us what's wrong. Reports are reviewed by our moderators.</p>
            <form onSubmit={handleReportSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5" htmlFor={`report-reason-${post.id}`}>Reason</label>
                <select
                  id={`report-reason-${post.id}`}
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 focus:border-cyan-400"
                  data-testid={`report-reason-${post.id}`}
                >
                  <option value="" disabled>Select a reason</option>
                  {REPORT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1.5" htmlFor={`report-details-${post.id}`}>Details (optional)</label>
                <textarea
                  id={`report-details-${post.id}`}
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Add any extra context..."
                  className="w-full px-4 py-2.5 bg-muted/40 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-cyan-400/25 resize-none"
                  data-testid={`report-details-${post.id}`}
                />
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <button type="button" onClick={() => setShowReport(false)} className="px-4 py-2 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer">Cancel</button>
                <button
                  type="submit"
                  disabled={!reportReason || reportSubmitting}
                  className="px-5 py-2 text-xs font-medium bg-red-500 hover:bg-red-600 text-white rounded-lg border-0 cursor-pointer disabled:opacity-40 transition-colors"
                  data-testid={`report-submit-${post.id}`}
                >
                  {reportSubmitting ? "Reporting..." : "Submit report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Block / Mute Confirmation */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setConfirmAction(null)} data-testid={`confirm-${confirmAction}-${post.id}`}>
          <div className="bg-card rounded-2xl shadow-xl max-w-sm w-full p-6 animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-outfit text-lg font-semibold text-foreground mb-2">
              {confirmAction === "block" ? `Block @${post.author_username}?` : `Mute @${post.author_username}?`}
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              {confirmAction === "block"
                ? "You won't see each other's posts or comments, and you'll be unfollowed from each other. They won't be notified."
                : "Their posts will be hidden from your feed. They won't know they've been muted."}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmAction(null)} className="px-4 py-2 text-xs text-muted-foreground bg-transparent border-0 cursor-pointer">Cancel</button>
              <button
                onClick={handleBlockMute}
                disabled={confirmBusy}
                className={`px-5 py-2 text-xs font-medium text-white rounded-lg border-0 cursor-pointer disabled:opacity-40 transition-colors ${confirmAction === "block" ? "bg-red-500 hover:bg-red-600" : "bg-indigo-500 hover:bg-indigo-600"}`}
                data-testid={`confirm-${confirmAction}-btn-${post.id}`}
              >
                {confirmBusy ? "Working..." : confirmAction === "block" ? "Block" : "Mute"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
